import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { connectorQueries } from "@/components/connectors/api.ts";
import { TEAMS_ADMIN_GUIDE_URL, type ConnectorSummary } from "@/types/connectors.ts";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

/** How long to watch for okthx's install, which a background job does after connect. */
const INSTALL_WAIT_MS = 30_000;

/** How often to re-read the connectors while watching. */
const POLL_MS = 3_000;

type TeamsConnector = ConnectorSummary & { teams: NonNullable<ConnectorSummary["teams"]> };

/** `connectors` that carry a Teams summary. */
function teamsConnectors(connectors: readonly ConnectorSummary[]): TeamsConnector[] {
  return connectors.flatMap((connector) =>
    connector.teams ? [{ ...connector, teams: connector.teams }] : [],
  );
}

/**
 * The Teams connector a just-finished connect is most likely about: the newest
 * one okthx isn't installed for yet, else the newest.
 */
export function pickTeamsConnector(
  connectors: readonly ConnectorSummary[],
): TeamsConnector | undefined {
  let newest: TeamsConnector | undefined;
  let newestPending: TeamsConnector | undefined;
  for (const connector of teamsConnectors(connectors)) {
    if (!newest || connector.id > newest.id) newest = connector;
    if (!connector.teams.botInstalled && (!newestPending || connector.id > newestPending.id)) {
      newestPending = connector;
    }
  }
  return newestPending ?? newest;
}

/** What the dialog says at each stage of the install. */
function installDescription(installed: boolean, waiting: boolean): string {
  if (installed) {
    return "Check Teams for a message from okthx, then message it there like you would a person.";
  }
  if (waiting) return "Adding okthx to your Teams…";
  return "okthx couldn't add itself to your Teams. Add it from Teams; if Teams says the app isn't available, your Teams admin needs to allow it.";
}

type Props = { onClose: () => void };

/**
 * "Add okthx to Teams", opened right after someone connects Teams. The connect
 * queues a job that installs okthx for them, so this watches for that install
 * for a while and offers the link to add it themselves when it doesn't land,
 * for instance when their tenant's policy needs an admin.
 */
export const TeamsInstallDialog = ({ onClose }: Props) => {
  const [waiting, setWaiting] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setWaiting(false), INSTALL_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  // The connector this dialog follows, chosen once from the first fresh read
  // so another Teams login's state never takes over; null when there is none.
  const [targetId, setTargetId] = useState<number | null>();
  const followed = (connectors: readonly ConnectorSummary[] | undefined) =>
    teamsConnectors(connectors ?? []).find((connector) => connector.id === targetId);

  const { data, isError, isFetchedAfterMount } = useQuery({
    ...connectorQueries.list(),
    refetchInterval: (query) =>
      waiting && !followed(query.state.data?.connectors)?.teams.botInstalled ? POLL_MS : false,
  });
  // Only from a list read since opening: one cached from before the connect
  // lacks the new connector, or describes an older one.
  if (targetId === undefined && data && isFetchedAfterMount) {
    setTargetId(pickTeamsConnector(data.connectors)?.id ?? null);
  }
  const connector = followed(data?.connectors);

  // Nothing to offer: the server predates the field, the first read failed, or
  // the followed connector was disconnected. A later poll failing keeps what is
  // already shown, link included.
  useEffect(() => {
    if (targetId === null || (targetId !== undefined && !connector) || (isError && !connector)) {
      onClose();
    }
  }, [targetId, isError, connector, onClose]);
  if (!connector) return null;

  const installed = connector.teams.botInstalled;
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex w-full max-w-md flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{installed ? "okthx is in your Teams" : "Add okthx to Teams"}</DialogTitle>
          <DialogDescription>{installDescription(installed, waiting)}</DialogDescription>
        </DialogHeader>
        {!installed && !waiting && (
          <a
            href={TEAMS_ADMIN_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            Guide for Teams admins
          </a>
        )}
        <DialogFooter>
          <Button
            variant="primary"
            size="sm"
            asChild
            data-statsig-name={installed ? "teams-install-open" : "teams-install-add"}
          >
            <a href={connector.teams.installUrl} target="_blank" rel="noopener noreferrer">
              {installed ? "Open Teams" : "Add to Teams"}
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
